import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { ClickOutDropDown } from '../../DropDown/ClickOutDropDown';
import ChevronLeftIcon from '../../Icons/ChevronLeftIcon';
import ChevronRightIcon from '../../Icons/ChevronRightIcon';
import PaginatorDownIcon from '../../Icons/PaginatorDownIcon';

interface TablePaginationProps {
  endPage: number;
  defaultNumOfPagination?: number;
  changePage: (...arg: any) => void;
  className?: string;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  endPage,
  changePage,
  className,
}) => {
  const [activePage, setActivePage] = useState<number>(1);

  useEffect(() => {
    if (endPage <= 0) {
      setActivePage(1);
      return;
    }

    if (activePage > endPage) {
      setActivePage(endPage);
      changePage(endPage);
    }
  }, [activePage, changePage, endPage]);

  const allPages = Array.from(
    { length: Math.max(0, endPage) },
    (_, index) => index + 1,
  );

  const updatePage = (nextPage: number) => {
    if (nextPage === activePage) {
      return;
    }

    setActivePage(nextPage);
    changePage(nextPage);
  };

  const handleRightClick = () => {
    const index = activePage < endPage ? activePage + 1 : activePage;
    updatePage(index);
  };

  const handleLeftClick = () => {
    const index = activePage > 1 ? activePage - 1 : activePage;
    updatePage(index);
  };

  const handleNumberClick = (pageNumber: number) => {
    updatePage(pageNumber);
  };

  return (
    <Container className={className ? className : ''}>
      <ClickOutDropDown
        maxHeight='100px'
        label={
          <DropDownLabel aria-label='Current page selector'>
            {activePage} <PaginatorDownIcon />
          </DropDownLabel>
        }
      >
        <DropDownContent>
          {allPages.map((pageNumber) => (
            <NumberBox
              type='button'
              className={activePage === pageNumber ? 'active' : ''}
              key={pageNumber}
              onClick={() => handleNumberClick(pageNumber)}
              aria-label={`Go to page ${pageNumber}`}
            >
              {pageNumber}
            </NumberBox>
          ))}
        </DropDownContent>
      </ClickOutDropDown>

      <GuideText>
        of {endPage} Page{endPage > 1 ? 's' : ''}
      </GuideText>
      <ArrowControls>
        <IconWrap
          onClick={handleLeftClick}
          aria-label='Go to previous page'
          disabled={activePage === 1}
        >
          <ChevronLeftIcon />
        </IconWrap>
        <IconWrap
          onClick={handleRightClick}
          aria-label='Go to next page'
          disabled={activePage === endPage}
        >
          <ChevronRightIcon />
        </IconWrap>
      </ArrowControls>
    </Container>
  );
};

export default TablePagination;

const Container = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: fit-content;
  margin-left: auto;
  margin-top: 16px;

  &.scrollable-table {
    position: sticky;
    left: 0;
    margin-right: auto;
    margin-left: 0;
  }

  @media (max-width: 884px) {
    gap: 8px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const IconWrap = styled.button`
  display: grid;
  place-content: center;
  border: none;
  background-color: transparent;

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const GuideText = styled.div`
  font-size: 14px;
  margin-right: 20px;
  color: #5D665F;

  @media (max-width: 480px) {
    margin-right: 0;
    font-size: 12px;
  }
`;

const ArrowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 884px) {
    gap: 12px;
  }
`;

const DropDownLabel = styled.div`
  padding: 3px 11px;
  border-radius: 8px;
  background-color: rgba(94, 99, 102, 0.08);
  color: rgba(139, 141, 151, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  overflow: auto;

  @media (max-width: 480px) {
    min-width: 60px;
    justify-content: space-between;
  }
`;

const DropDownContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 56px;
  max-height: 220px;
  overflow-y: auto;

  @media (max-width: 480px) {
    width: 64px;
  }
`;

const NumberBox = styled.button`
  display: flex;
  justify-content: center;
  font-size: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  width: 100%;
  background: transparent;
  padding: 6px 0;

  &:hover {
    background-color: #FAF9F5;
  }

  &.active {
    background-color: #FAF9F5;
  }
`;
